import { useEffect, useState } from 'react'
import { loadThumbnail, type Thumbnail } from '@core/attachments'

/**
 * A small preview of page one, for lists.
 *
 * Being able to recognise a document at a glance — "that's the blue Sibelga
 * envelope" — is most of why the original matters in an overview. The number
 * beside it is the AI's opinion; this is the thing itself.
 */
export function DocumentThumb({
  documentId,
  onOpen,
  className = '',
}: {
  documentId: string
  onOpen?: () => void
  className?: string
}) {
  const [thumb, setThumb] = useState<Thumbnail | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    loadThumbnail(documentId)
      .then((t) => alive && setThumb(t))
      .catch(() => alive && setFailed(true))
    return () => {
      alive = false
    }
  }, [documentId])

  const base = `shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-line grid place-items-center ${className}`

  if (failed || (thumb === null && !onOpen)) {
    return <div className={base} aria-hidden />
  }

  const isPdf = thumb?.contentType === 'application/pdf'

  const content = !thumb ? (
    <div className="w-full h-full animate-pulse bg-line" />
  ) : isPdf ? (
    // A PDF gets a label rather than a preview: it is a real document, not a
    // failed image, and it should not look like one.
    <span className="text-[0.65rem] font-medium text-muted tracking-wide">PDF</span>
  ) : (
    <img
      src={thumb.url}
      alt=""
      className="w-full h-full object-cover"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )

  if (!onOpen) return <div className={base}>{content}</div>

  return (
    <button onClick={onOpen} className={base} aria-label="Origineel document openen">
      {content}
    </button>
  )
}
